<script setup lang="ts">
import type { CvCreateMode } from '~/types'

/**
 * The three ways to start a CV on the homepage hero: an interview with the
 * AI, one block of text (pasted or written in one go) or the classic editor
 * form. Sits above each mode's headline, so it stays put when switching.
 */
const mode = defineModel<CvCreateMode>({ required: true })

const { t } = useI18n()

const modes = [
  { id: 'chat', icon: 'bi-chat-dots' },
  { id: 'paste', icon: 'bi-clipboard-check' },
  { id: 'form', icon: 'bi-ui-checks' },
] as const satisfies readonly { id: CvCreateMode, icon: string }[]
</script>

<template>
  <div
    role="tablist"
    :aria-label="t('cvModes.label')"
    class="mode-switch mx-auto flex w-full max-w-[46rem] overflow-x-auto rounded-full border border-lilac bg-lilac/30 p-1 dark:border-lilac/50 dark:bg-lilac/10"
  >
    <!-- Lilac track with a dark active pill: reads as a switch, and never as
         the green call to action below it. -->
    <button
      v-for="item in modes"
      :key="item.id"
      type="button"
      role="tab"
      :aria-selected="mode === item.id"
      class="inline-flex flex-1 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-semibold transition-colors duration-300 sm:gap-2 sm:px-4 sm:text-base"
      :class="mode === item.id
        ? 'bg-ink text-white shadow-sm dark:bg-lilac dark:text-ink'
        : 'text-ink hover:bg-lilac/60 dark:text-gray-200 dark:hover:bg-lilac/25'"
      @click="mode = item.id"
    >
      <i class="bi" :class="item.icon" />
      <span class="sm:hidden">{{ t(`cvModes.${item.id}.short`) }}</span>
      <span class="hidden sm:inline">{{ t(`cvModes.${item.id}.label`) }}</span>
    </button>
  </div>
</template>

<style scoped>
.mode-switch {
  scrollbar-width: none;
}

.mode-switch::-webkit-scrollbar {
  display: none;
}
</style>
